/**
 * Settings › Sandbox › "Docker images": the sandbox's Docker image cache (services/DockerCacheService.js). Shown only
 * where the plan has one (with Docker); each image with its size and last use, Keep (never removed automatically) and
 * Remove (now; refused while a container uses it). Above the plan's size the sandbox removes the least recently used
 * images that are not kept.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Switch } from 'antd';
import { getDockerCache, keepImage, removeImage } from '../../services/DockerCacheService';
import { formatBytes } from '../../utils/dataModel';
import { formatRelativeTime } from '../../utils/relativeTime';

export function imageName(image) {
  const tag = (image.tags || []).find((t) => t && t !== '<none>:<none>');
  return tag || `${String(image.id || '').replace(/^sha256:/, '').slice(0, 12)} (untagged)`;
}

export default function DockerImageCache({ load = getDockerCache, keep = keepImage, remove = removeImage }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(() => load().then(setData).catch((err) => setError(err.message)), [load]);
  useEffect(() => { refresh(); }, [refresh]);

  if (!data || data.available === false || !data.enabled) { return null; }
  const act = async (id, fn) => {
    setBusy(id);
    setError(null);
    try { await fn(); await refresh(); } catch (err) { setError(err.message); } finally { setBusy(null); }
  };
  const used = data.usedBytes || 0;
  return (
    <div aria-labelledby="set-sandbox-docker" className="msb-section" data-testid="docker-cache" role="group">
      <h3 id="set-sandbox-docker">Docker images</h3>
      <p className="settings-help">
        {`${formatBytes(used)} of ${formatBytes(data.limitBytes)} used. Images stay when your sandbox sleeps; above the limit the least recently used ones are removed, except the ones you keep.`}
      </p>
      {error && <p className="msb-status msb-status-error" role="alert">{error}</p>}
      {(data.images || []).length === 0 && <p className="settings-help">No images yet.</p>}
      {(data.images || []).map((image) => (
        <div className="settings-row" data-testid={`docker-image-${image.id}`} key={image.id}>
          <span className="settings-label">
            <strong>{imageName(image)}</strong>
            <span>
              {formatBytes(image.bytes)}
              {image.inUse ? ' · in use' : image.lastUsedMs ? ` · used ${formatRelativeTime(image.lastUsedMs)}` : ' · not used yet'}
            </span>
          </span>
          <Switch
            aria-label={`Keep ${imageName(image)}`}
            checked={Boolean(image.keep)}
            checkedChildren="Keep"
            disabled={busy === image.id}
            onChange={(value) => act(image.id, () => keep(image.id, value))}
            unCheckedChildren="Keep"
          />
          <Button
            disabled={image.inUse || busy === image.id}
            onClick={() => act(image.id, () => remove(image.id))}
            title={image.inUse ? 'A service uses this image: stop it first' : undefined}
          >
            Remove
          </Button>
        </div>
      ))}
    </div>
  );
}

DockerImageCache.propTypes = { keep: PropTypes.func, load: PropTypes.func, remove: PropTypes.func };
